import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip, Warning } from '../../components/LessonUI';

const JSClosures = () => {
    const basicClosureCode = `function createCounter() {
    let count = 0; // "Persistent" variable in Lexical Environment
    
    return function() {
        count++; // Accesses 'count' via closure
        return count;
    };
}

const increment = createCounter();
console.log(increment()); // 1
console.log(increment()); // 2`;

    const staleClosureCode = `// The "Stale Closure" Pitfall (Common in React)
function setupCounter() {
    let count = 0;
    
    const increment = () => {
        count++;
        console.log('Current:', count);
    };

    const logCount = () => {
        // If this function is "captured" elsewhere, 
        // it might still reference an old version of 'count'
        console.log('Logged:', count);
    };

    return { increment, logCount };
}`;

    const memoryLeakCode = `// Memory Leak Example: Detached Closures
function createHeavyProcess() {
    const largeData = new Array(1000000).fill('');
    
    return function getMetadata() {
        // This closure keeps the ENTIRE largeData in memory 
        // even if it only needs the length.
        return largeData.length;
    };
}

const getLen = createHeavyProcess();
// LargeData is now "leaked" into the getLen closure scope.`;

    const stateMachineCode = `// Architectural Pattern: Closure-based State Machine
const createTrafficLight = () => {
    let state = 'RED';

    return {
        next() {
            const transitions = {
                'RED': 'GREEN',
                'GREEN': 'YELLOW',
                'YELLOW': 'RED'
            };
            state = transitions[state];
            return state;
        },
        getState: () => state
    };
};`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Engine Internals</div>
                <h1>Closures & Lexical Scope</h1>
                <p className="hero-lead">
                    Closures are often described as "mysterious," but they are a fundamental
                    byproduct of how JavaScript handles scope. Master them to unlock advanced
                    patterns in <strong>encapsulation</strong> and <strong>functional
                        programming</strong>.
                </p>
            </div>

            <div className="content-section">
                <h2>Defining the Closure</h2>
                <p>
                    A <strong>Closure</strong> is the combination of a function bundled
                    together with references to its surrounding state (the <strong>Lexical
                        Environment</strong>). In simpler terms, a closure gives a function
                    access to its outer scope even after the outer function has finished
                    executing.
                </p>
                <CodeBlock language="javascript" code={basicClosureCode} />
                <ProTip><strong> Pro Tip:</strong> Every function in JavaScript is technically
                    a closure, as they all have access to the global scope. However, we
                    usually use the term to describe functions that "save" a local
                    variable from a finished execution context.</ProTip>
            </div>

            <div className="content-section">
                <h2>The Lexical Environment</h2>
                <p>
                    When the JS engine creates an <strong>Execution Context</strong>, it
                    also creates a <strong>Lexical Environment</strong>. This record stores
                    identifier-variable mappings. When a function is defined, it "captures"
                    a reference to its parent's Lexical Environment, creating a
                    <strong>Scope Chain</strong>.
                </p>
                <div className="key-point">
                    Building a mental model: Think of a closure as a "backpack" that the
                    function carries around, containing all the variables that were in
                    scope when the function was created.
                </div>
            </div>

            <div className="content-section">
                <h2>The "Stale Closure" Problem</h2>
                <p>
                    One of the most common bugs in modern React development is the
                    <strong>Stale Closure</strong>. This occurs when a closure captures
                    variables that change over time, but the closure itself is not
                    re-created to reflect those changes.
                </p>
                <CodeBlock language="javascript" code={staleClosureCode} />
                <Warning><strong> React Alert:</strong> This is why the <strong>dependency
                        array</strong> in hooks like <code>useEffect</code> or <code>useCallback</code>
                    is so critical. It tells React when to "refresh" the closure so it doesn't
                    reference stale data.</Warning>
            </div>

            <div className="content-section">
                <h2>Memory & Garbage Collection</h2>
                <p>
                    Closures can lead to unintentional <strong>Memory Leaks</strong>. Because
                    the inner function maintains a reference to the outer scope, the outer
                    variables cannot be <strong>Garbage Collected</strong> (GC'd) as long
                    as the inner function exists.
                </p>
                <CodeBlock language="javascript" code={memoryLeakCode} />
                <div className="best-practice-box">
                    <strong>Performance Hack:</strong> If you only need a small piece of
                    data from a large object inside a closure, extract that specific
                    variable into a local primitive. This allows the large object to be
                    safely GC'd.
                </div>
            </div>

            <div className="content-section">
                <h2>Architectural Excellence</h2>
                <p>
                    Closures are the foundation of <strong>Private State</strong>. By
                    returning functions that interact with local variables, you create
                    robust APIs that cannot be tampered with by external code.
                </p>
                <CodeBlock language="javascript" code={stateMachineCode} />
            </div>

            <KeyFacts><ul>
                    <li><strong>Persistent State:</strong> Use closures to maintain state without globals.</li>
                    <li><strong>Encapsulation:</strong> Hide implementation details from the public API.</li>
                    <li> <strong>Watch the Memory:</strong> Avoid capturing large objects you don't fully need.</li>
                    <li> <strong>Identify Stale Data:</strong> Ensure closures are "re-hydrated" if their captured variables change.</li>
                    <li><strong>Debug like a Pro:</strong> Use the <code>[[Scopes]]</code> property in Chrome DevTools to inspect closures.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSClosures;


